import {
  CalendarClock,
  ClipboardCheck,
  ClipboardList,
  PackageCheck,
} from 'lucide-react'
import {
  isToolError,
  pickString,
  plural,
  readToolState,
  ToolListPreview,
  ToolStatusLine,
  type ToolPartProps,
} from './_base'

export function AuditPackageReadinessToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const score = state.result?.readinessScore
  const canSubmit = state.result?.canSubmit === true
  const blockers: Array<Record<string, unknown>> = Array.isArray(
    state.result?.blockers
  )
    ? state.result.blockers
    : []
  const warnings: Array<Record<string, unknown>> = Array.isArray(
    state.result?.warnings
  )
    ? state.result.warnings
    : []

  const completedLabel =
    state.isComplete && !isToolError(state)
      ? canSubmit
        ? 'Audit package ready'
        : `${plural(blockers.length, 'blocker')} before submission`
      : 'Audit package checked'

  // Name the cycle: a multi-framework workspace has one per framework, and a
  // bare "72% ready" is ambiguous about which package it describes.
  const cycleDetail = pickString(
    state.result?.auditCycle?.label,
    state.result?.auditCycle?.framework
  )
  const detail = [
    cycleDetail,
    typeof score === 'number' ? `${Math.round(score)}% ready` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Checking audit package readiness...'
        completedLabel={completedLabel}
        completedDetail={detail || null}
        completedIcon={
          <PackageCheck className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
        }
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='Nothing blocking'
          items={[...blockers, ...warnings].map((finding, index) => ({
            id: null,
            title: pickString(finding.title) ?? `Finding ${index + 1}`,
            // The resolution is the actionable half — surfacing only the
            // title would make the list a wall of problems with no next step.
            subtitle: pickString(finding.resolution, finding.description),
            meta: [
              typeof finding.severity === 'string' ? finding.severity : null,
              typeof finding.category === 'string' ? finding.category : null,
              typeof finding.controlRef === 'string'
                ? finding.controlRef
                : null,
            ],
            tone:
              finding.severity === 'blocker'
                ? ('critical' as const)
                : ('medium' as const),
          }))}
        />
      ) : null}
    </>
  )
}

export function ProgramGetStateToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  let detail: string | null = null
  const r = state.result
  if (r) {
    const parts: string[] = []
    if (typeof r.policiesGenerated === 'number')
      parts.push(`${r.policiesGenerated} policies`)
    if (typeof r.controlsTotal === 'number')
      parts.push(`${r.controlsAnswered ?? 0}/${r.controlsTotal} controls`)
    if (typeof r.openTickets === 'number')
      parts.push(`${plural(r.openTickets, 'open ticket')}`)
    detail = parts.length ? parts.join(', ') : null
  }
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Loading program state...'
      completedLabel='Program state loaded'
      completedDetail={detail}
      completedIcon={
        <ClipboardList className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
      }
    />
  )
}

export function ProgramGetReadinessToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const score = state.result?.score
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Assessing readiness...'
      completedLabel='Readiness assessment loaded'
      completedDetail={
        typeof score === 'number' ? `${Math.round(score * 100)}% ready` : null
      }
      completedIcon={
        <ClipboardCheck className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
      }
    />
  )
}

export function ProgramGetTimelineToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Loading audit timeline...'
      completedLabel='Audit timeline loaded'
      completedDetail={pickString(
        state.result?.cycleLabel,
        state.result?.auditCycleLabel
      )}
      completedIcon={
        <CalendarClock className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
      }
    />
  )
}

